import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Grid, Stack, InputLabel, Skeleton } from '@mui/material'
import { useTheme } from '@mui/styles'

import Input from 'components/generic/form/Input'
import NotesInput from 'components/generic/inputs/NotesInput'
import FileUploader from 'components/generic/file-uploader/FileUploader'

import { formatNumber, setDecimalPrecision } from 'utils/helpers'

const inputFields = [
  { name: 'footerInformation.freightCost', label: 'Freight:' },
  { name: 'footerInformation.grossWeight', label: 'Gross:' },
  { name: 'footerInformation.tareWeight', label: 'Tare:' },
  { name: 'footerInformation.netWeight', label: 'Net:' },
]

const OutboundTicketFooter = ({
  loadInformation,
  onClickNotes,
  notes,
  files,
  onUploadFiles,
  disabledUploadFiles,
  onRemoveFiles,
  setOpenFreightCostBlade,
  isLoadingFreightLanesData,
  viewOnly,
  carrierPricePrepend,
}) => {
  const theme = useTheme()
  const methods = useFormContext()
  const { setValue, control, watch } = methods
  const cost = watch('freightCost.cost')

  const { canUploadFilesOBT } = useSelector(state => state.featureFlags?.variables || {})
  const { defaultYardUm } = useSelector(state => state.settings)

  const [fieldLengths, setFieldLengths] = useState({
    'footerInformation.freightCost': 0,
    'footerInformation.grossWeight': 0,
    'footerInformation.tareWeight': 0,
    'footerInformation.netWeight': 0,
  })

  useEffect(() => {
    const formattedFreightCost = `${carrierPricePrepend}${setDecimalPrecision(cost || 0, { decimalPrecisionOverride: 2 })}`
    const formattedGrossWeight = `${formatNumber(loadInformation.loadWeight.gross)} ${defaultYardUm}`
    const formattedTareWeight = `${formatNumber(loadInformation.loadWeight.tare)} ${defaultYardUm}`
    const formattedNetWeight = `${formatNumber(loadInformation.loadWeight.net)} ${defaultYardUm}`
    setValue('footerInformation.freightCost', formattedFreightCost)
    setValue('footerInformation.grossWeight', formattedGrossWeight)
    setValue('footerInformation.tareWeight', formattedTareWeight)
    setValue('footerInformation.netWeight', formattedNetWeight)
    setFieldLengths({
      'footerInformation.freightCost': formattedFreightCost.length,
      'footerInformation.grossWeight': formattedGrossWeight.length,
      'footerInformation.tareWeight': formattedTareWeight.length,
      'footerInformation.netWeight': formattedNetWeight.length,
    })
  }, [loadInformation, cost, carrierPricePrepend, defaultYardUm])

  return (
    <>
      <Grid
        sx={{
          padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
          bottom: 0,
          zIndex: 2,
          position: 'sticky',
          background: theme.palette.background.default,
        }}
        borderTop={1}
        borderColor="divider"
        data-testid="footer-information"
      >
        <Stack direction="row" alignItems="center" spacing={theme.spacing(1)}>
          <NotesInput
            sx={{
              flex: 4,
              // TODO: Remove this line when remove canUploadFilesOBT FF
              paddingRight: canUploadFilesOBT ? 0 : theme.spacing(3),
            }}
            notes={notes}
            onClickNotes={onClickNotes}
          />
          {canUploadFilesOBT && (
            <div style={{ marginRight: '1.5em' }}>
              <FileUploader
                onUploadFiles={onUploadFiles}
                onRemoveFiles={onRemoveFiles}
                filesData={files}
                disabled={disabledUploadFiles}
                viewOnly={viewOnly}
                tooltipMsg="To attach files, please save the outbound ticket first"
              />
            </div>
          )}
          {inputFields.map(field => (
            <Stack sx={{ flex: 1 }} key={field?.name}>
              {field?.label === 'Freight:' && isLoadingFreightLanesData ? (
                <>
                  <InputLabel shrink>Freight</InputLabel>
                  <Skeleton animation="wave" sx={{ width: `${fieldLengths[field?.name]}ch` }} />
                </>
              ) : (
                <Input
                  name={field?.name}
                  label={field?.label}
                  control={control}
                  variant="standard"
                  defaultValue=""
                  sx={{
                    '& .MuiInput-root': {
                      cursor: 'unset',
                    },
                    input: {
                      WebkitTextFillColor:
                        field?.label === 'Freight:' ? theme.palette.primary.main : theme.palette.text.primary,
                      fontWeight: 500,
                      width: `${fieldLengths[field?.name]}ch`,
                      textDecoration: field?.label === 'Freight:' ? 'underline' : 'unset',
                      cursor: field?.label === 'Freight:' ? 'pointer' : 'text',
                    },
                    label: {
                      WebkitTextFillColor: theme.palette.text.secondary,
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    inputProps: {
                      onClick: () => {
                        if (field?.label !== 'Freight:') return
                        setOpenFreightCostBlade(true)
                      },
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Stack>
          ))}
        </Stack>
      </Grid>
    </>
  )
}

OutboundTicketFooter.propTypes = {
  loadInformation: PropTypes.object.isRequired,
  onClickNotes: PropTypes.func.isRequired,
  notes: PropTypes.array,
  onUploadFiles: PropTypes.func.isRequired,
  onRemoveFiles: PropTypes.func.isRequired,
  files: PropTypes.array,
  disabledUploadFiles: PropTypes.bool,
  setOpenFreightCostBlade: PropTypes.func.isRequired,
  isLoadingFreightLanesData: PropTypes.bool,
  viewOnly: PropTypes.bool,
  carrierPricePrepend: PropTypes.string,
}

OutboundTicketFooter.defaultProps = {
  notes: [],
  files: [],
  disabledUploadFiles: false,
  isLoadingFreightLanesData: false,
  viewOnly: false,
  carrierPricePrepend: '$',
}

export default OutboundTicketFooter
